import type { SelectProps } from '@lokalise/louis';
import { Select } from '@lokalise/louis';
import { useField } from 'formik';
import type { Ref } from 'react';
import { forwardRef } from 'react';
import type { GroupBase, SelectInstance } from 'react-select';

type SelectFieldProps<
    Async extends boolean,
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
> = SelectProps<Async, Creatable, OptionType, IsMulti, GroupType> & {
    // Reason we do this instead of `Omit` is because `Omit` flattens the types but we want to preserve
    // the advanced types. By specifying `error: never`, we remove it from props.
    error?: never;
    name: string;
};

const SelectField = <
    Async extends boolean,
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
    { name, ...props }: SelectFieldProps<Async, Creatable, OptionType, IsMulti, GroupType>,
    ref: Ref<SelectInstance>,
) => {
    const [field, { touched, error }, { setValue, setTouched }] = useField({ name, type: 'select' });

    return (
        <Select
            ref={ref}
            {...field}
            onBlur={() => setTouched(true)}
            onChange={(value) => setValue(value)}
            error={touched ? error : undefined}
            components={{ ...props.components }} // Make sure that components will be an empty object instead of undefined to satisfy Typescript
            menuPosition="fixed"
            {...props}
        />
    );
};

// The solution comes from: https://fettblog.eu/typescript-react-generic-forward-refs/#option-1%3A-type-assertion
const ForwardedSelectFieldComponent = forwardRef(SelectField) as <
    Async extends boolean,
    Creatable extends boolean,
    OptionType,
    IsMulti extends boolean = false,
    GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
    props: SelectFieldProps<Async, Creatable, OptionType, IsMulti, GroupType> & {
        ref?: Ref<SelectInstance>;
    },
) => ReturnType<typeof SelectField>;

export default ForwardedSelectFieldComponent;
